import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface MapSnapshotState {
  roomId: string | null;
  snapshot: Record<string, unknown> | null;
  hydrated: boolean;
  /** Wire/persistence envelope metadata. Absent on legacy raw snapshots. */
  protocolVersion?: number | null;
  epoch?: string | null;
  version?: number | null;
  checksum?: string | null;
  /** Bounded generation tombstones prevent delayed iframe messages regressing state. */
  retiredEpochs?: string[];
  /** Raw persisted v1 bytes awaiting an ordered v0 server import. */
  pendingLegacySnapshot?: Record<string, unknown> | null;
  /** Full persisted v2 bytes retained until live authority proves/imports them. */
  pendingCanonicalSnapshot?: Record<string, unknown> | null;
  /** Authority paired with raw bytes in a mixed-rollout checkpoint. */
  pendingLegacyAuthorityReference?: MapSnapshotAuthorityReference | null;
  /** Authority that proves the full canonical restore candidate. */
  pendingCanonicalAuthorityReference?: MapSnapshotAuthorityReference | null;
  /** Metadata-only persisted precondition with no restore bytes. */
  pendingAuthorityReference?: MapSnapshotAuthorityReference | null;
}

export interface MapSnapshotAuthorityReference {
  protocolVersion: 2;
  epoch: string;
  version: number;
  checksum: string;
}

const initialState: MapSnapshotState = {
  roomId: null,
  snapshot: null,
  hydrated: false,
  protocolVersion: null,
  epoch: null,
  version: null,
  checksum: null,
  retiredEpochs: [],
  pendingLegacySnapshot: null,
  pendingCanonicalSnapshot: null,
  pendingLegacyAuthorityReference: null,
  pendingCanonicalAuthorityReference: null,
  pendingAuthorityReference: null,
};

interface RoomSnapshotPayload {
  roomId: string;
  snapshot: Record<string, unknown>;
  protocolVersion?: unknown;
  epoch?: unknown;
  version?: unknown;
  checksum?: unknown;
}

type RoomSnapshotReferencePayload = Omit<RoomSnapshotPayload, 'snapshot'>;

const MAP_CHECKSUM_PATTERN = /^sha256:[a-f0-9]{64}$/;
const MAP_EPOCH_PATTERN = /^[A-Za-z0-9._:-]{1,256}$/;

function assignEnvelopeMetadata(state: MapSnapshotState, payload: RoomSnapshotPayload) {
  state.protocolVersion =
    typeof payload.protocolVersion === 'number' &&
    Number.isSafeInteger(payload.protocolVersion) &&
    payload.protocolVersion > 0
      ? payload.protocolVersion
      : null;
  state.epoch =
    typeof payload.epoch === 'string' && MAP_EPOCH_PATTERN.test(payload.epoch)
      ? payload.epoch
      : null;
  state.version =
    typeof payload.version === 'number' &&
    Number.isSafeInteger(payload.version) &&
    payload.version >= 0
      ? payload.version
      : null;
  state.checksum =
    typeof payload.checksum === 'string' &&
    MAP_CHECKSUM_PATTERN.test(payload.checksum)
      ? payload.checksum
      : null;
}

function canonicalPayloadMetadata(payload: RoomSnapshotPayload) {
  if (
    payload.protocolVersion !== 2 ||
    typeof payload.epoch !== 'string' ||
    !MAP_EPOCH_PATTERN.test(payload.epoch) ||
    typeof payload.version !== 'number' ||
    !Number.isSafeInteger(payload.version) ||
    payload.version < 0 ||
    typeof payload.checksum !== 'string' ||
    !MAP_CHECKSUM_PATTERN.test(payload.checksum)
  ) {
    return null;
  }
  return {
    epoch: payload.epoch,
    version: payload.version,
    checksum: payload.checksum,
  };
}

function hasCanonicalState(state: MapSnapshotState): boolean {
  return Boolean(
    state.protocolVersion === 2 &&
      typeof state.epoch === 'string' &&
      MAP_EPOCH_PATTERN.test(state.epoch) &&
      typeof state.version === 'number' &&
      Number.isSafeInteger(state.version) &&
      state.version >= 0 &&
      typeof state.checksum === 'string' &&
      MAP_CHECKSUM_PATTERN.test(state.checksum),
  );
}

function retireCurrentEpoch(state: MapSnapshotState, nextEpoch: string) {
  if (!Array.isArray(state.retiredEpochs)) state.retiredEpochs = [];
  if (!state.epoch || state.epoch === nextEpoch || state.retiredEpochs.includes(state.epoch)) return;
  state.retiredEpochs.push(state.epoch);
  if (state.retiredEpochs.length > 32) {
    state.retiredEpochs.splice(0, state.retiredEpochs.length - 32);
  }
}

function applyCanonicalPayload(
  state: MapSnapshotState,
  payload: RoomSnapshotPayload,
  allowSameTupleRepair: boolean,
) {
  const incoming = canonicalPayloadMetadata(payload);
  if (!incoming) {
    if (hasCanonicalState(state)) return;
    state.snapshot = payload.snapshot;
    assignEnvelopeMetadata(state, payload);
    return;
  }
  if (!Array.isArray(state.retiredEpochs)) state.retiredEpochs = [];
  if (state.retiredEpochs.includes(incoming.epoch)) return;
  if (hasCanonicalState(state)) {
    if (state.epoch === incoming.epoch) {
      if (incoming.version < (state.version as number)) return;
      if (incoming.version === state.version) {
        if (!allowSameTupleRepair) return;
      }
    } else {
      retireCurrentEpoch(state, incoming.epoch);
    }
  }
  state.snapshot = payload.snapshot;
  assignEnvelopeMetadata(state, payload);
}

function preserveHydratedLegacyBeforeCanonicalAuthority(
  state: MapSnapshotState,
  payload: RoomSnapshotPayload,
) {
  const incoming = canonicalPayloadMetadata(payload);
  if (
    incoming &&
    state.hydrated &&
    !hasCanonicalState(state) &&
    state.snapshot
  ) {
    state.pendingLegacySnapshot = state.snapshot;
  }
}

function clearPendingAuthorityProvenByLiveState(state: MapSnapshotState) {
  if (!hasCanonicalState(state)) return;
  const isProven = (pending: MapSnapshotAuthorityReference | null | undefined) => Boolean(
    pending &&
      state.epoch === pending.epoch &&
      (
        (state.version as number) > pending.version ||
        (state.version === pending.version && state.checksum === pending.checksum)
      ),
  );
  if (isProven(state.pendingCanonicalAuthorityReference)) {
    state.pendingCanonicalSnapshot = null;
    state.pendingCanonicalAuthorityReference = null;
  }
  if (isProven(state.pendingAuthorityReference)) {
    state.pendingAuthorityReference = null;
  }
  // A canonical checksum does not describe raw legacy bytes. Only an explicit
  // import/equivalence acknowledgement may clear that independent candidate.
}

const mapSnapshotSlice = createSlice({
  name: 'mapSnapshot',
  initialState,
  reducers: {
    setMapSnapshotRoom: (state, action: PayloadAction<{ roomId: string }>) => {
      if (!action.payload.roomId || state.roomId === action.payload.roomId) return;
      return {
        roomId: action.payload.roomId,
        snapshot: null,
        hydrated: false,
        protocolVersion: null,
        epoch: null,
        version: null,
        checksum: null,
        retiredEpochs: [],
        pendingLegacySnapshot: null,
        pendingCanonicalSnapshot: null,
        pendingLegacyAuthorityReference: null,
        pendingCanonicalAuthorityReference: null,
        pendingAuthorityReference: null,
      };
    },
    hydrateMapSnapshot: (state, action: PayloadAction<RoomSnapshotPayload>) => {
      if (!action.payload.roomId) return;
      // A late response for the previous call must never replace state that is
      // already bound to the newly selected room.
      if (state.roomId && state.roomId !== action.payload.roomId) return;
      const incoming = canonicalPayloadMetadata(action.payload);
      const alreadyCanonical = hasCanonicalState(state);
      const hadHydratedLegacy = state.hydrated && !alreadyCanonical && Boolean(state.snapshot);
      state.roomId = action.payload.roomId;
      state.hydrated = true;
      if (incoming) {
        state.pendingCanonicalSnapshot = action.payload.snapshot;
        state.pendingCanonicalAuthorityReference = {
          protocolVersion: 2,
          ...incoming,
        };
      }
      // Persisted state is seed material only. Once live canonical authority
      // exists, a delayed hydrate may mark completion but cannot replace it.
      if (alreadyCanonical) {
        // A delayed raw persisted snapshot may be the only remaining copy of
        // legacy edits, even if a mixed-rollout client already advanced the
        // canonical room. Keep it beside (never in place of) live authority;
        // import/finalize will prove equivalence or fail closed on divergence.
        if (!incoming) {
          state.pendingLegacySnapshot = action.payload.snapshot;
        }
      } else {
        if (incoming && hadHydratedLegacy && state.snapshot) {
          state.pendingLegacySnapshot = state.snapshot;
        }
        applyCanonicalPayload(state, action.payload, false);
      }
    },
    markMapSnapshotHydrated: (state, action: PayloadAction<{ roomId: string }>) => {
      if (!action.payload.roomId) return;
      if (state.roomId && state.roomId !== action.payload.roomId) return;
      state.roomId = action.payload.roomId;
      state.hydrated = true;
    },
    hydrateMapSnapshotReference: (
      state,
      action: PayloadAction<RoomSnapshotReferencePayload>,
    ) => {
      if (!action.payload.roomId) return;
      if (state.roomId && state.roomId !== action.payload.roomId) return;
      const reference = {
        ...action.payload,
        snapshot: {},
      };
      if (!canonicalPayloadMetadata(reference)) return;
      const metadata = canonicalPayloadMetadata(reference)!;
      state.roomId = action.payload.roomId;
      state.hydrated = true;
      state.pendingAuthorityReference = {
        protocolVersion: 2,
        ...metadata,
      };
      // A persisted reference is a fail-closed precondition, not snapshot
      // bytes. Preserve newer live authority if it already arrived.
      if (!hasCanonicalState(state)) assignEnvelopeMetadata(state, reference);
    },
    hydratePendingLegacyMapSnapshot: (
      state,
      action: PayloadAction<RoomSnapshotPayload>,
    ) => {
      if (!action.payload.roomId) return;
      if (state.roomId && state.roomId !== action.payload.roomId) return;
      state.roomId = action.payload.roomId;
      state.hydrated = true;
      state.pendingLegacySnapshot = action.payload.snapshot;
      const pairedAuthority = canonicalPayloadMetadata(action.payload);
      if (pairedAuthority) {
        state.pendingLegacyAuthorityReference = {
          protocolVersion: 2,
          ...pairedAuthority,
        };
      }
      if (!hasCanonicalState(state)) {
        if (pairedAuthority) {
          // A mixed-rollout checkpoint pairs the raw base with the exact
          // canonical tuple. Preserve that pairing without pretending the raw
          // bytes match the canonical checksum.
          assignEnvelopeMetadata(state, action.payload);
        } else {
          state.snapshot = action.payload.snapshot;
        }
      }
    },
    updateMapSnapshot: (state, action: PayloadAction<RoomSnapshotPayload>) => {
      if (!action.payload.roomId) return;
      if (state.roomId && state.roomId !== action.payload.roomId) return;
      state.roomId = action.payload.roomId;
      preserveHydratedLegacyBeforeCanonicalAuthority(state, action.payload);
      applyCanonicalPayload(state, action.payload, false);
      clearPendingAuthorityProvenByLiveState(state);
    },
    repairMapSnapshot: (state, action: PayloadAction<RoomSnapshotPayload>) => {
      if (!action.payload.roomId || !canonicalPayloadMetadata(action.payload)) return;
      if (state.roomId && state.roomId !== action.payload.roomId) return;
      state.roomId = action.payload.roomId;
      preserveHydratedLegacyBeforeCanonicalAuthority(state, action.payload);
      // Correlated full snapshots/checksum repair may replace bytes at the
      // same authority tuple; ordering and retired-generation gates still hold.
      applyCanonicalPayload(state, action.payload, true);
      clearPendingAuthorityProvenByLiveState(state);
    },
    clearPendingMapRestore: (
      state,
      action: PayloadAction<{
        roomId: string;
        kind: 'legacy' | 'canonical' | 'reference' | 'all';
      }>,
    ) => {
      if (state.roomId !== action.payload.roomId) return;
      if (action.payload.kind === 'legacy' || action.payload.kind === 'all') {
        state.pendingLegacySnapshot = null;
        state.pendingLegacyAuthorityReference = null;
      }
      if (action.payload.kind === 'canonical' || action.payload.kind === 'all') {
        state.pendingCanonicalSnapshot = null;
        state.pendingCanonicalAuthorityReference = null;
      }
      if (action.payload.kind === 'reference' || action.payload.kind === 'all') {
        state.pendingAuthorityReference = null;
      }
    },
    // Compatibility action for older bridge code. It is intentionally
    // selective now: importing raw bytes cannot prove a canonical candidate.
    clearPendingLegacyMapSnapshot: (state, action: PayloadAction<{ roomId: string }>) => {
      if (state.roomId !== action.payload.roomId) return;
      state.pendingLegacySnapshot = null;
      state.pendingLegacyAuthorityReference = null;
    },
    clearMapSnapshot: () => ({
      ...initialState,
      retiredEpochs: [],
      pendingLegacySnapshot: null,
      pendingCanonicalSnapshot: null,
      pendingLegacyAuthorityReference: null,
      pendingCanonicalAuthorityReference: null,
      pendingAuthorityReference: null,
    }),
  },
});

export const {
  setMapSnapshotRoom,
  hydrateMapSnapshot,
  markMapSnapshotHydrated,
  hydrateMapSnapshotReference,
  hydratePendingLegacyMapSnapshot,
  updateMapSnapshot,
  repairMapSnapshot,
  clearPendingMapRestore,
  clearPendingLegacyMapSnapshot,
  clearMapSnapshot,
} =
  mapSnapshotSlice.actions;

export default mapSnapshotSlice.reducer;
